import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { CheckboxModule } from "primeng/checkbox";
import { CommonModule } from "@angular/common";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import {
  userAuthenticationRestClientInjectionToken
} from "../../rest-clients/user/authentication/user-authentication-rest-client-injection-token";
import {
  IUserAuthenticationRestClient
} from "../../rest-clients/user/authentication/i-user-authentication-rest.client";
import {
  sessionStateServiceInjectionToken
} from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import {
  ContentSlideComponent,
  ContentSliderComponent,
  ContentSliderService,
  contentSliderServiceInjectionToken,
  ContentSliderStateService,
  contentSliderStateServiceInjectionToken,
  ESeverity,
  IContentSliderService,
  IContentSliderStateService,
  IModalService,
  IToastService,
  modalServiceInjectionToken,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { ensureBillingCustomer } from "../billing-country-modal/ensure-billing-customer";
import { dictionaryServiceInjectionToken } from "@zaeper/angular-localization-lib";
import { IDictionaryService } from "@zaeper/localization-lib";
import { firstValueFrom } from "rxjs";
import { LicencePriceComponent } from "../../components/licence-price/licence-price.component";
import { StripeService } from "../../services/stripe/stripe.service";
import { HttpErrorResponse } from "@angular/common/http";
import { IDeveloperBillingRestClient } from "../../rest-clients/billing/i-developer-billing-rest.client";
import { developerBillingRestClientInjectionToken } from "../../rest-clients/billing/developer-billing-rest-client-injection-token";
import type { Stripe, StripeElements, StripePaymentElement } from "@stripe/stripe-js";

@Component({
  selector: "app-developer-registration-modal",
  templateUrl: "./developer-registration-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    ContentSliderComponent,
    ContentSlideComponent,
    LicencePriceComponent
  ],
  providers: [
    {
      provide: contentSliderStateServiceInjectionToken,
      useClass: ContentSliderStateService
    },
    {
      provide: contentSliderServiceInjectionToken,
      useClass: ContentSliderService
    }
  ]
})
export class DeveloperRegistrationModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild("paymentElement") paymentElementRef!: ElementRef<HTMLDivElement>;

  public displayName: string = "";
  public website: string = "";
  public slug: string = "";
  /** Live check of the slug: format first, then availability from the server. */
  public slugStatus: "empty" | "invalid" | "checking" | "available" | "taken" | "reserved" = "empty";
  public termsAccepted: boolean = false;
  public isSubmitting: boolean = false;
  public isLoadingPayment: boolean = false;
  public isConfirmingPayment: boolean = false;
  public errorMessage: string | null = null;
  public paymentErrorMessage: string | null = null;
  public activeSlideIndex: number = 0;
  /** Registered (terms accepted) but the licence isn't paid yet: only the payment step is left. */
  public isPending: boolean = false;

  private _stripe: Stripe | null = null;
  private _elements: StripeElements | null = null;
  private _paymentElement: StripePaymentElement | null = null;
  private _clientSecret: string | null = null;
  private _stripeSubscriptionId: string | null = null;
  private _slugCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private _slugCheckSequence: number = 0;

  /** The rule of registry namespaces (lowercase letters, digits and single "-", at most 39 characters). */
  private static readonly SLUG_PATTERN: RegExp = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  private static readonly SLUG_MAX_LENGTH: number = 39;
  /** The registration is activated by the backend from the paid subscription; this long the modal waits for it. */
  private static readonly ACTIVATION_ATTEMPTS: number = 10;
  private static readonly ACTIVATION_INTERVAL_MS: number = 1500;

  constructor(
    @Inject(userAuthenticationRestClientInjectionToken) private readonly _userAuthenticationRestClient: IUserAuthenticationRestClient,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(contentSliderServiceInjectionToken) private readonly _contentSliderService: IContentSliderService,
    @Inject(contentSliderStateServiceInjectionToken) private readonly _contentSliderStateService: IContentSliderStateService,
    @Inject(developerBillingRestClientInjectionToken) private readonly _billingRestClient: IDeveloperBillingRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    @Inject(modalServiceInjectionToken) private readonly _modalService: IModalService,
    @Inject(dictionaryServiceInjectionToken) private readonly _dictionaryService: IDictionaryService,
    private readonly _stripeService: StripeService,
    private readonly _dynamicDialogRef: DynamicDialogRef,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this._contentSliderStateService.activeSlideIndex$.subscribe((index) => {
      this.activeSlideIndex = index;
      this._cdr.markForCheck();
    });
  }

  /** After the view: the slides are counted and the payment element exists. */
  async ngAfterViewInit(): Promise<void> {
    const currentUser = this._sessionStateService.getCurrentUser();
    if (currentUser === null || currentUser.termsAcceptedAt === null) {
      return;
    }
    // Registered before: the licence may be paid meanwhile (a payment redirect, a renewal), else pay now.
    const returnedFromPayment: boolean = new URLSearchParams(window.location.search).get("redirect_status") === "succeeded";
    if (await this._activate(returnedFromPayment ? DeveloperRegistrationModalComponent.ACTIVATION_ATTEMPTS : 1)) {
      return;
    }
    this.isPending = true;
    this._cdr.markForCheck();
    this._contentSliderService.nextSlide();
    await this._initPaymentStep();
  }

  ngOnDestroy(): void {
    this._paymentElement?.destroy();
    if (this._slugCheckTimer) {
      clearTimeout(this._slugCheckTimer);
    }
  }

  public get isRegistrationFormValid(): boolean {
    return this.displayName.trim().length > 0 && this.termsAccepted && this.slugStatus === "available";
  }

  public get slugMessage(): string | null {
    switch (this.slugStatus) {
      case "invalid":
        return "Use lowercase letters, digits and single hyphens (at most 39 characters).";
      case "checking":
        return "Checking availability…";
      case "available":
        return `${this.slug} is available.`;
      case "taken":
        return `${this.slug} is already taken.`;
      case "reserved":
        return `${this.slug} is reserved.`;
      default:
        return null;
    }
  }

  public onSlugChange(value: string): void {
    this.slug = value.trim().toLowerCase();
    if (this._slugCheckTimer) {
      clearTimeout(this._slugCheckTimer);
    }
    if (this.slug.length === 0) {
      this.slugStatus = "empty";
    } else if (this.slug.length > DeveloperRegistrationModalComponent.SLUG_MAX_LENGTH
      || !DeveloperRegistrationModalComponent.SLUG_PATTERN.test(this.slug)) {
      this.slugStatus = "invalid";
    } else {
      this.slugStatus = "checking";
      this._slugCheckTimer = setTimeout(() => this._checkSlug(this.slug), 300);
    }
    this._cdr.markForCheck();
  }

  private async _checkSlug(slug: string): Promise<void> {
    const sequence: number = ++this._slugCheckSequence;
    try {
      const availability = await this._userAuthenticationRestClient.checkSlugAvailability(slug);
      if (sequence !== this._slugCheckSequence || slug !== this.slug) {
        return;
      }
      this.slugStatus = availability.available
        ? "available"
        : availability.reason === "slug_not_claimable" ? "reserved" : "taken";
    } catch (e) {
      if (sequence === this._slugCheckSequence) {
        this.slugStatus = "empty";
        this.errorMessage = "Could not check the slug. Please try again.";
      }
    }
    this._cdr.markForCheck();
  }

  public async onRegister(): Promise<void> {
    if (!this.isRegistrationFormValid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = null;
    this._cdr.markForCheck();

    try {
      const currentUser = await this._userAuthenticationRestClient.registerDeveloper({
        displayName: this.displayName.trim(),
        website: this.website.trim() || undefined,
        termsAccepted: this.termsAccepted,
        slug: this.slug
      });

      this._sessionStateService.setCurrentUser(currentUser);
      this.isSubmitting = false;
      this.isPending = true;
      this._cdr.markForCheck();

      // A licence paid before the registration activates it right away.
      if (currentUser.activatedAt !== null) {
        this._toastService.setMessage({
          title: "Welcome!",
          detail: "Your developer license is now active.",
          severity: ESeverity.success
        });
        this._dynamicDialogRef.close(true);
        return;
      }

      this._contentSliderService.nextSlide();

      await this._initPaymentStep();
    } catch (e) {
      const code: string | undefined = e instanceof HttpErrorResponse ? e.error?.error : undefined;
      if (code === "slug_taken") {
        this.slugStatus = "taken";
        this.errorMessage = null;
      } else if (code === "slug_not_claimable") {
        this.slugStatus = "reserved";
        this.errorMessage = null;
      } else if (code === "account_has_namespace") {
        this.errorMessage = "Your account already has a different slug.";
      } else {
        this.errorMessage = "Registration failed. Please try again.";
      }
      this.isSubmitting = false;
      this._cdr.markForCheck();
    }
  }

  public async onConfirmPayment(): Promise<void> {
    if (!this._stripe || !this._elements || this.isConfirmingPayment) return;

    this.isConfirmingPayment = true;
    this.paymentErrorMessage = null;
    this._cdr.markForCheck();

    const { error } = await this._stripe.confirmPayment({
      elements: this._elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: "if_required"
    });

    if (error) {
      this.paymentErrorMessage = error.message ?? "Payment failed. Please try again.";
      this.isConfirmingPayment = false;
      this._cdr.markForCheck();
    } else {
      if (this._stripeSubscriptionId) {
        // Fetches the paid subscription from Stripe right away instead of waiting for the webhook.
        await this._billingRestClient.syncLicense(this._stripeSubscriptionId).catch(() => undefined);
      }
      if (!(await this._activate(DeveloperRegistrationModalComponent.ACTIVATION_ATTEMPTS))) {
        this.paymentErrorMessage = "Payment received. Activating your license takes a moment, please reload the page shortly.";
        this.isConfirmingPayment = false;
        this._cdr.markForCheck();
      }
    }
  }

  /** Asks the backend to activate the registration; true (and the modal closes) once it is active. */
  private async _activate(attempts: number): Promise<boolean> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, DeveloperRegistrationModalComponent.ACTIVATION_INTERVAL_MS));
      }
      try {
        const currentUser = await this._userAuthenticationRestClient.activateDeveloperRegistration();
        if (currentUser.activatedAt !== null) {
          this._sessionStateService.setCurrentUser(currentUser);
          this._toastService.setMessage({
            title: "Welcome!",
            detail: "Your developer license is now active.",
            severity: ESeverity.success
          });
          this._dynamicDialogRef.close(true);
          return true;
        }
      } catch (e) {
        // Retried below; the caller shows the payment step or a message.
      }
    }
    return false;
  }

  public goBack(): void {
    this._contentSliderService.prevSlide();
  }

  private async _initPaymentStep(): Promise<void> {
    this.isLoadingPayment = true;
    this.paymentErrorMessage = null;
    this._cdr.markForCheck();

    try {
      // The billing country decides the currency of the licence; it is asked once, before the first payment.
      if (!(await ensureBillingCustomer(this._modalService, this._billingRestClient))) {
        this.paymentErrorMessage = await firstValueFrom( this._dictionaryService.getTranslation( "billing-country-required", "main" ) );
        this.isLoadingPayment = false;
        this._cdr.markForCheck();
        return;
      }

      this._stripe = await this._stripeService.getStripe();
      if (!this._stripe) {
        this.paymentErrorMessage = "Could not load payment provider.";
        this.isLoadingPayment = false;
        this._cdr.markForCheck();
        return;
      }

      // The backend bills the yearly developer licence; the price is never chosen here.
      const subscription = await this._billingRestClient.subscribeLicense({
        items: [{
          productKey: "developer.license",
          quantity: 1
        }]
      });

      this._clientSecret = subscription.clientSecret;
      this._stripeSubscriptionId = subscription.stripeSubscriptionId;

      if (!this._clientSecret) {
        this.paymentErrorMessage = "Could not initialize payment. Please try again.";
        this.isLoadingPayment = false;
        this._cdr.markForCheck();
        return;
      }

      const isDark = document.documentElement.classList.contains("app-dark");
      const computedStyle = getComputedStyle(document.documentElement);
      const primaryColor = computedStyle.getPropertyValue("--color-zaeper-primary-500").trim() || (isDark ? "#a5b4fc" : "#6366f1");
      const primaryColorLight = computedStyle.getPropertyValue("--color-zaeper-primary-400").trim() || "#a5b4fc";

      this._elements = this._stripe.elements({
        clientSecret: this._clientSecret,
        appearance: {
          theme: isDark ? "night" : "flat",
          variables: {
            fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
            fontSizeBase: "14px",
            fontWeightNormal: "400",
            fontWeightMedium: "500",
            borderRadius: "8px",
            colorPrimary: isDark ? primaryColorLight : primaryColor,
            colorBackground: isDark ? "#1e293b" : "#ffffff",
            colorText: isDark ? "#e2e8f0" : "#334155",
            colorTextSecondary: isDark ? "#94a3b8" : "#64748b",
            colorDanger: "#ef4444",
            spacingUnit: "4px",
            spacingGridRow: "16px",
            spacingGridColumn: "16px"
          },
          rules: {
            ".Input": {
              border: isDark ? "1px solid #334155" : "1px solid #e2e8f0",
              boxShadow: "none",
              padding: "10px 12px"
            },
            ".Input:focus": {
              border: `1px solid ${primaryColor}`,
              boxShadow: `0 0 0 1px ${primaryColor}`
            },
            ".Label": {
              fontWeight: "500",
              marginBottom: "6px"
            },
            ".Tab": {
              border: isDark ? "1px solid #334155" : "1px solid #e2e8f0",
              borderRadius: "8px",
              justifyContent: "center"
            },
            ".Tab:hover": {
              backgroundColor: isDark ? "#334155" : "#f1f5f9"
            },
            ".Tab--selected": {
              borderColor: primaryColor,
              backgroundColor: isDark ? "#1e1b4b" : "#eef2ff",
              color: isDark ? primaryColorLight : primaryColor
            },
            ".Tab--selected:hover": {
              backgroundColor: isDark ? "#1e1b4b" : "#eef2ff",
              color: isDark ? primaryColorLight : primaryColor
            },
            ".Tab--selected .TabIcon": {
              fill: isDark ? primaryColorLight : primaryColor
            },
            ".Tab--selected .TabLabel": {
              color: isDark ? primaryColorLight : primaryColor
            },
            ".TabIcon--selected": {
              fill: isDark ? primaryColorLight : primaryColor
            },
            ".Badge": {
              display: "none"
            },
            ".Link": {
              color: isDark ? primaryColorLight : primaryColor
            },
            ".Link .LinkIcon": {
              fill: isDark ? primaryColorLight : primaryColor
            }
          }
        }
      });

      this._paymentElement = this._elements.create("payment", {
        layout: "tabs"
      });

      this._paymentElement.mount(this.paymentElementRef.nativeElement);

      this.isLoadingPayment = false;
      this._cdr.markForCheck();
    } catch (e) {
      // The licence is paid already (paid in another tab, or the webhook was slow): activate instead of paying twice.
      if (e instanceof HttpErrorResponse && e.status === 409 && e.error?.error === "already_licensed"
        && await this._activate(DeveloperRegistrationModalComponent.ACTIVATION_ATTEMPTS)) {
        return;
      }
      this.paymentErrorMessage = "Could not load payment form. Please try again.";
      this.isLoadingPayment = false;
      this._cdr.markForCheck();
    }
  }
}
